import { CredoAppService } from "@credolab/credoapp-sdk";
import { setCookie } from "@/shared-helpers/set-cookie.helper";
import { getCookieHelper } from "@/shared-helpers/get-cookie.helper";
import { AndroidInterface } from "@/apps/core-app-onboarding/services/android.service";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";

const authKey = process.env.VUE_APP_CREDOLAB_AUTHKEY;
const url = process.env.VUE_APP_CREDOLAB_API_URL;
const baseDomain = process.env.VUE_APP_BASE_DOMAIN;
const frontendPrefix = "PJO_frontend_";
const nativePrefix = "PJO_native_";

const frontendSuffix = "_frontend";
const nativeSuffix = "_device";
const referenceNumberCookieKeyName = "credolabReferenceNumber";
const credolabSentCookieKeyName = "credolabSendingCompleted";
declare const Android: AndroidInterface;

export function setCredolabCookieReferenceNumber(
  referenceNumber: string | number
): void {
  setCredolabCookie(referenceNumberCookieKeyName, referenceNumber);
}

export function setCredolabCookieCompleted(name: string): void {
  setCredolabCookie(name, Math.floor(Date.now() / 1000));
}

function setCredolabCookie(
  name: string,
  value: string | number,
  expires = 5
): void {
  setCookie(name, value, expires, "/", buildDomainForCookie());
}

function buildDomainForCookie(): string {
  return baseDomain;
}

export function stopAndSendCredolabFrontendData(
  passedContext: Record<string, string | number> = {}
): boolean {
  const referenceNumber = getCredolabCookieReferenceNumber();
  const completedTime = getCredolabSendingCompletedTime(
    buildFrontendDataSentCookieName(frontendSuffix)
  );

  const context = Object.assign({}, passedContext, {
    referenceNumber: referenceNumber,
    completedTime: completedTime,
  });

  if (completedTime) {
    logDataDogInfo("referenceNumber already submitted to credoLab", context);
    return false;
  }
  if (referenceNumber) {
    const result = stopAndSendCredolab(referenceNumber);
    if (result) {
      logDataDogInfo("frontend stopAndSendCredolab returned true", context);
      return true;
    }
  }
  logDataDogInfo(
    "stopAndSendCredolabFrontendData: credoLab data not sent",
    context
  );
  return false;
}

export function getCredolabCookieReferenceNumber(): string | undefined {
  return getCookieHelper(referenceNumberCookieKeyName);
}

export function getCredolabSendingCompletedTime(
  name: string
): string | undefined {
  return getCookieHelper(name);
}

function buildDeviceDataSentCookieName(
  suffix: string,
  context: Record<string, string | number> = {}
): string {
  let cookieName = credolabSentCookieKeyName + suffix;
  if (context && "caller" in context) {
    cookieName += cookieName + "_" + toCamelCase(String(context["caller"]));
  }
  return cookieName;
}

function buildFrontendDataSentCookieName(suffix: string): string {
  return credolabSentCookieKeyName + suffix;
}

function toCamelCase(text: string): string {
  text = text.toLowerCase();
  return text
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word : word.toUpperCase();
    })
    .replace(/\s+/g, "");
}

export function sendCredolabDeviceReport(
  passedContext: Record<string, string | number> = {}
): boolean {
  if (typeof Android.getCreditLineApplicationId !== "function") {
    logDataDogInfo(
      "getCreditLineApplicationId not a function. probably PJM",
      passedContext
    );
    return false;
  }

  const applicationId = Android.getCreditLineApplicationId();
  const alreadySentCookieName = buildDeviceDataSentCookieName(
    nativeSuffix,
    passedContext
  );
  const completedTime = getCredolabSendingCompletedTime(alreadySentCookieName);
  const referenceNumber = nativePrefix + applicationId;
  const context = Object.assign({}, passedContext, {
    referenceNumber: referenceNumber,
    completedTime: completedTime,
  });

  if (completedTime) {
    logDataDogInfo("referenceNumber already submitted to credoLab", context);
    return false;
  }
  if (typeof Android.sendRiskScoringResults === "function") {
    Android.sendRiskScoringResults();
    setCredolabCookieCompleted(alreadySentCookieName);
    logDataDogInfo("native sendCredolabDeviceReport sent", context);
    return true;
  }
  logDataDogInfo(
    "Android.sendRiskScoringResults not supported, native sendCredolabDeviceReport not sent",
    context
  );
  return false;
}

export function getCredolabCookie(): string | undefined {
  return getCookieHelper("credolabReferenceNumber");
}

export function initializeCredolab(referenceNumber: string | number): boolean {
  try {
    const credoAppService = new CredoAppService(url, authKey);
    credoAppService.startTracking();
    setCredolabCookieReferenceNumber(referenceNumber);
    return true;
  } catch (error) {
    logDataDogInfo("initializeCredolab error: " + error, {
      referenceNumber: referenceNumber,
    });
  }
  return false;
}

export async function stopAndSendCredolab(
  referenceNumber: string | number
): Promise<boolean> {
  let result = false;
  const credoAppService = new CredoAppService(url, authKey);
  const alreadySentCookieName = buildFrontendDataSentCookieName(frontendSuffix);

  await credoAppService
    .stopTrackingAndCompleteAsync(frontendPrefix + referenceNumber)
    .then(
      () => {
        setCredolabCookieCompleted(alreadySentCookieName);
        result = true;
      },
      (e) =>
        logDataDogInfo("stopAndSendCredolab error: " + e, {
          referenceNumber: frontendPrefix + referenceNumber,
        })
    );
  return result;
}

function logDataDogInfo(
  message: string,
  passedContext: Record<string, string | number> = {}
) {
  const applicationId =
    typeof Android.getCreditLineApplicationId === "function"
      ? Android.getCreditLineApplicationId()
      : null;
  const context = Object.assign({}, passedContext, {
    applicationId: applicationId,
  });

  console.log(message);
  DatadogLogger.Log(message, context, LogType.INFO);
}
